import React, { useState, useEffect } from "react";
import {
	Calendar,
	Clock,
	MapPin,
	Users,
	Edit3,
	Check,
	X,
	Plus,
	Trash2,
} from "lucide-react";
import { useApp } from "../components/AppContext";
import { useAuth } from "../components/AuthContext";
import {
	formatDateDisplay,
	formatTime,
	formatTimeRange,
} from "../utils/dateUtils";
import { MainLayout } from "../components/MainLayout";

// Event status system
export type EventStatus =
	| "CONTRACT_MISSING"
	| "PRE_WORK_CHECKIN_READY"
	| "READY_FOR_WORK"
	| "APPROVED"
	| "PAYROLLED";

export interface EventStatusConfig {
	text: string;
	class: string;
	color: string;
	bgColor: string;
	description: string;
}

export const eventStatuses: Record<EventStatus, EventStatusConfig> = {
	CONTRACT_MISSING: {
		text: "Contract Missing",
		class: "status-contract-missing",
		color: "var(--ucm-error-red)",
		bgColor: "rgba(239, 68, 68, 0.1)",
		description: "Complete contract data to proceed",
	},
	PRE_WORK_CHECKIN_READY: {
		text: "Pre-work Check-in Ready",
		class: "status-checkin-ready",
		color: "var(--ucm-warning-orange)",
		bgColor: "var(--ucm-light-orange)",
		description: "Ready for pre-work check-in",
	},
	READY_FOR_WORK: {
		text: "Ready for work",
		class: "status-ready-work",
		color: "var(--ucm-info-blue)",
		bgColor: "var(--ucm-light-blue)",
		description: "All requirements completed, ready to work",
	},
	APPROVED: {
		text: "Approved",
		class: "status-approved",
		color: "var(--ucm-success-green)",
		bgColor: "var(--ucm-light-green)",
		description: "Event participation approved",
	},
	PAYROLLED: {
		text: "Payrolled",
		class: "status-payrolled",
		color: "var(--ucm-booked-purple)",
		bgColor: "rgba(124, 58, 237, 0.1)",
		description: "Payment processed successfully",
	},
};

export interface EventTask {
	id: string;
	title: string;
	description: string;
	duration: number;
}

export interface EventData {
	id: string;
	title: string;
	date: string;
	startTime: string;
	endTime: string;
	location: string;
	description: string;
	tasks: EventTask[];
	status: EventStatus;
}

export function EventDetails() {
	const { navigateDirectly, userCompletionState, updateOnboardingState } =
		useApp();

	// Determine event status based on user completion state
	function determineEventStatus(
		date?: string,
		startTime?: string
	): EventStatus {
		if (!userCompletionState.contract_complete) {
			return "CONTRACT_MISSING";
		}

		// Check if within 24 hours of event
		if (date && startTime) {
			const eventDateTime = new Date(`${date}T${startTime}`);
			const now = new Date();
			const hoursDiff =
				(eventDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);

			if (
				userCompletionState.contract_complete &&
				!userCompletionState.checked_in &&
				hoursDiff <= 24 &&
				hoursDiff > 0
			) {
				return "PRE_WORK_CHECKIN_READY";
			}

			if (
				userCompletionState.checked_in &&
				!userCompletionState.event_confirmed
			) {
				return "READY_FOR_WORK";
			}

			if (userCompletionState.event_confirmed && hoursDiff < 0) {
				return "APPROVED";
			}
		}

		// Mock payroll status - in real app this would be determined by backend
		const mockPayrollProcessed = false;
		if (mockPayrollProcessed) {
			return "PAYROLLED";
		}

		return "CONTRACT_MISSING";
	}

	// Mock event data - in a real app, this would come from props or API
	const [eventData, setEventData] = useState<EventData>({
		id: "3",
		title: "Frankfurt Airport Security Inspector",
		date: "2026-01-08",
		startTime: "14:00",
		endTime: "22:00",
		location: "Terminal 3 - Security Checkpoint",
		description:
			"Airport security inspection operations for passenger screening and baggage checks.",
		tasks: [
			{
				id: "1",
				title: "Passenger Screening",
				description:
					"Screen passengers through metal detectors and X-ray machines",
				duration: 120,
			},
			{
				id: "2",
				title: "Baggage Inspection",
				description: "Manual inspection of suspicious luggage items",
				duration: 90,
			},
			{
				id: "3",
				title: "Documentation Review",
				description: "Verify travel documents and boarding passes",
				duration: 60,
			},
		],
		status: determineEventStatus("2026-01-08", "14:00"),
	});

	// Edit states
	const [isEditingDate, setIsEditingDate] = useState(false);
	const [isEditingTasks, setIsEditingTasks] = useState(false);
	const [editEventData, setEditEventData] = useState<EventData>(eventData);

	// User permissions - in real app, this would come from user context
	const canEdit = true; // For demo purposes, allow editing

	// Set hasCompletedOnboarding to true when user reaches this screen
	useEffect(() => {
		updateOnboardingState({
			hasCompletedOnboarding: true,
			isFirstTimeUser: false,
		});
	}, []);

	const calculateDuration = (startTime: string, endTime: string) => {
		const start = new Date(`2000-01-01T${startTime}`);
		const end = new Date(`2000-01-01T${endTime}`);
		const diffMs = end.getTime() - start.getTime();
		const diffHours = diffMs / (1000 * 60 * 60);
		return `${diffHours} hours`;
	};

	const handleSaveDateChanges = () => {
		setEventData(editEventData);
		setIsEditingDate(false);
	};

	const handleCancelDateEdit = () => {
		setEditEventData(eventData);
		setIsEditingDate(false);
	};

	const handleSaveTaskChanges = () => {
		setEventData(editEventData);
		setIsEditingTasks(false);
	};

	const handleCancelTaskEdit = () => {
		setEditEventData(eventData);
		setIsEditingTasks(false);
	};

	const handleAddTask = () => {
		const newTask: EventTask = {
			id: Date.now().toString(),
			title: "",
			description: "",
			duration: 30,
		};
		setEditEventData((prev) => ({
			...prev,
			tasks: [...prev.tasks, newTask],
		}));
	};

	const handleRemoveTask = (taskId: string) => {
		setEditEventData((prev) => ({
			...prev,
			tasks: prev.tasks.filter((task) => task.id !== taskId),
		}));
	};

	const handleTaskChange = (
		taskId: string,
		field: keyof EventTask,
		value: string | number
	) => {
		setEditEventData((prev) => ({
			...prev,
			tasks: prev.tasks.map((task) =>
				task.id === taskId ? { ...task, [field]: value } : task
			),
		}));
	};

	const currentStatus = eventStatuses[eventData.status];

	return (
		<MainLayout showBack={true} onBack={() => navigateDirectly("dashboard")}>
			<div className="px-6 py-8">
				{/* Event Header with Status */}
				<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)] mb-6">
					<div className="text-center">
						<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-4">
							{eventData.title}
						</h1>

						{/* Status Badge */}
						<div className="flex justify-center mb-4">
							<div
								className="inline-flex items-center px-4 py-2 rounded-full border font-semibold"
								style={{
									color: currentStatus.color,
									backgroundColor: currentStatus.bgColor,
									borderColor: currentStatus.color,
								}}
							>
								<span className="text-sm">{currentStatus.text}</span>
							</div>
						</div>

						<p className="text-sm text-[var(--ucm-text-medium)] mb-2">
							{currentStatus.description}
						</p>

						<div className="flex items-center justify-center text-[var(--ucm-text-medium)] mb-4">
							<MapPin size={16} className="mr-2" />
							<span className="text-sm">{eventData.location}</span>
						</div>
					</div>
				</div>

				{/* Date & Time Section */}
				<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)] mb-6">
					<div className="flex items-center justify-between mb-4">
						<h3 className="font-semibold text-[var(--ucm-text-dark)]">
							Datum & Zeit
						</h3>
						{canEdit && (
							<button
								onClick={() => {
									if (isEditingDate) {
										handleSaveDateChanges();
									} else {
										setEditEventData(eventData);
										setIsEditingDate(true);
									}
								}}
								className="flex items-center space-x-2 px-3 py-1 text-sm bg-[var(--ucm-primary-teal)] text-white rounded-lg hover:bg-[var(--ucm-primary-teal-hover)] transition-colors"
							>
								{isEditingDate ? <Check size={16} /> : <Edit3 size={16} />}
								<span>{isEditingDate ? "Speichern" : "Bearbeiten"}</span>
							</button>
						)}
					</div>

					{!isEditingDate ? (
						<div className="space-y-3">
							<div className="flex items-center">
								<Calendar
									size={18}
									className="text-[var(--ucm-text-light)] mr-3"
								/>
								<div>
									<p className="font-medium text-[var(--ucm-text-dark)]">
										{formatDateDisplay(eventData.date)}
									</p>
									<p className="text-sm text-[var(--ucm-text-medium)]">
										{eventData.date}
									</p>
								</div>
							</div>
							<div className="flex items-center">
								<Clock
									size={18}
									className="text-[var(--ucm-text-light)] mr-3"
								/>
								<div>
									<p className="font-medium text-[var(--ucm-text-dark)]">
										{formatTime(eventData.startTime)} -{" "}
										{formatTime(eventData.endTime)}
									</p>
									<p className="text-sm text-[var(--ucm-text-medium)]">
										Dauer:{" "}
										{calculateDuration(eventData.startTime, eventData.endTime)}
									</p>
								</div>
							</div>
						</div>
					) : (
						<div className="space-y-4">
							<div className="grid grid-cols-1 gap-4">
								<div>
									<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
										Probebetriebsdatum
									</label>
									<input
										type="date"
										value={editEventData.date}
										onChange={(e) =>
											setEditEventData((prev) => ({
												...prev,
												date: e.target.value,
											}))
										}
										className="w-full px-4 py-3 border border-[var(--ucm-border-light)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent"
									/>
								</div>
								<div className="grid grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											Startzeit
										</label>
										<input
											type="time"
											value={editEventData.startTime}
											onChange={(e) =>
												setEditEventData((prev) => ({
													...prev,
													startTime: e.target.value,
												}))
											}
											className="w-full px-4 py-3 border border-[var(--ucm-border-light)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent"
										/>
									</div>
									<div>
										<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
											Endzeit
										</label>
										<input
											type="time"
											value={editEventData.endTime}
											onChange={(e) =>
												setEditEventData((prev) => ({
													...prev,
													endTime: e.target.value,
												}))
											}
											className="w-full px-4 py-3 border border-[var(--ucm-border-light)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent"
										/>
									</div>
								</div>
							</div>
							<div className="flex space-x-3">
								<button
									onClick={handleCancelDateEdit}
									className="flex items-center space-x-2 px-4 py-2 text-sm border border-[var(--ucm-border-medium)] text-[var(--ucm-text-medium)] rounded-lg hover:bg-[var(--ucm-disabled-gray)] transition-colors"
								>
									<X size={16} />
									<span>Abbrechen</span>
								</button>
							</div>
						</div>
					)}
				</div>

				{/* Tasks & Activities Section */}
				<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)] mb-6">
					<div className="flex items-center justify-between mb-4">
						<h3 className="font-semibold text-[var(--ucm-text-dark)]">
							Aufgaben & Aktivitäten
						</h3>
						{canEdit && (
							<button
								onClick={() => {
									if (isEditingTasks) {
										handleSaveTaskChanges();
									} else {
										setEditEventData(eventData);
										setIsEditingTasks(true);
									}
								}}
								className="flex items-center space-x-2 px-3 py-1 text-sm bg-[var(--ucm-primary-teal)] text-white rounded-lg hover:bg-[var(--ucm-primary-teal-hover)] transition-colors"
							>
								{isEditingTasks ? <Check size={16} /> : <Edit3 size={16} />}
								<span>{isEditingTasks ? "Speichern" : "Bearbeiten"}</span>
							</button>
						)}
					</div>

					{!isEditingTasks ? (
						<div className="space-y-4">
							{eventData.tasks.map((task, index) => (
								<div
									key={task.id}
									className="border border-[var(--ucm-border-light)] rounded-xl p-4"
								>
									<div className="flex items-start justify-between">
										<div className="flex-1">
											<h4 className="font-medium text-[var(--ucm-text-dark)] mb-2">
												{index + 1}. {task.title}
											</h4>
											<p className="text-sm text-[var(--ucm-text-medium)] mb-2">
												{task.description}
											</p>
											<span className="inline-flex items-center px-2 py-1 text-xs bg-[var(--ucm-light-blue)] text-[var(--ucm-dark-blue)] rounded-full">
												{task.duration} Minuten
											</span>
										</div>
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="space-y-4">
							{editEventData.tasks.map((task, index) => (
								<div
									key={task.id}
									className="border border-[var(--ucm-border-light)] rounded-xl p-4"
								>
									<div className="space-y-3">
										<div className="flex items-center justify-between">
											<span className="text-sm font-medium text-[var(--ucm-text-medium)]">
												Aufgabe {index + 1}
											</span>
											<button
												onClick={() => handleRemoveTask(task.id)}
												className="text-[var(--ucm-error-red)] hover:bg-red-50 p-1 rounded"
											>
												<Trash2 size={16} />
											</button>
										</div>
										<div>
											<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-1">
												Aufgabentitel
											</label>
											<input
												type="text"
												value={task.title}
												onChange={(e) =>
													handleTaskChange(task.id, "title", e.target.value)
												}
												className="w-full px-3 py-2 border border-[var(--ucm-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent"
												placeholder="Aufgabentitel eingeben"
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-1">
												Beschreibung
											</label>
											<textarea
												value={task.description}
												onChange={(e) =>
													handleTaskChange(
														task.id,
														"description",
														e.target.value
													)
												}
												rows={2}
												className="w-full px-3 py-2 border border-[var(--ucm-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent resize-none"
												placeholder="Aufgabenbeschreibung eingeben"
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-1">
												Dauer (Minuten)
											</label>
											<input
												type="number"
												value={task.duration}
												onChange={(e) =>
													handleTaskChange(
														task.id,
														"duration",
														parseInt(e.target.value) || 0
													)
												}
												min="5"
												max="480"
												className="w-full px-3 py-2 border border-[var(--ucm-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent"
											/>
										</div>
									</div>
								</div>
							))}

							<button
								onClick={handleAddTask}
								className="w-full flex items-center justify-center space-x-2 py-3 border-2 border-dashed border-[var(--ucm-border-medium)] rounded-xl text-[var(--ucm-text-medium)] hover:border-[var(--ucm-primary-teal)] hover:text-[var(--ucm-primary-teal)] transition-colors"
							>
								<Plus size={20} />
								<span>Aufgabe hinzufügen</span>
							</button>

							<div className="flex space-x-3">
								<button
									onClick={handleCancelTaskEdit}
									className="flex items-center space-x-2 px-4 py-2 text-sm border border-[var(--ucm-border-medium)] text-[var(--ucm-text-medium)] rounded-lg hover:bg-[var(--ucm-disabled-gray)] transition-colors"
								>
									<X size={16} />
									<span>Abbrechen</span>
								</button>
							</div>
						</div>
					)}
				</div>

				{/* Event Description */}
				<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
					<h3 className="font-semibold text-[var(--ucm-text-dark)] mb-3">
						Terminbeschreibung
					</h3>
					<p className="text-[var(--ucm-text-medium)] leading-relaxed">
						{eventData.description}
					</p>
				</div>
			</div>
		</MainLayout>
	);
}
